import Container, { Service } from 'typedi';
import { WindowToken } from '../../injection-tokens';
import { LoggerProvider } from '../logger';
import { ILogger } from '@sparkware/uc-sdk-core';

@Service()
export class GNWPUtils {
    private readonly _window: Window;
    private readonly _logger: ILogger;

    constructor() {
        this._window = Container.get(WindowToken);
        this._logger = Container.get(LoggerProvider).getLogger('GNWPUtils');
    }

    /**
     * Creates a form, adds it to the document body, and optionally submits it.
     * @param path - The action URL for the form
     * @param params - Object containing form fields with key/value pairs
     * @param method - HTTP method (default: 'post')
     * @param target - Target window for form submission (default: '_blank')
     * @param windowReference - Optional reference to another window where the form should be appended
     * @returns The created form element
     */
    public addFormToBody(
        path: string,
        params: Record<string, string>,
        method?: string,
        target?: string,
        windowReference?: Window,
    ): HTMLFormElement {
        try {
            const defaultTarget = '_blank';
            method = method || 'post';
            target = target || defaultTarget;

            this._logger.debug(`Adding form to body with path: ${path}`);

            const win = windowReference || this._window;
            const form = win.document.createElement('form');
            form.setAttribute('method', method);
            form.setAttribute('action', path);
            form.setAttribute('target', target);

            for (const key in params) {
                if (Object.prototype.hasOwnProperty.call(params, key)) {
                    const hiddenField = win.document.createElement('input');
                    hiddenField.setAttribute('type', 'hidden');
                    hiddenField.setAttribute('name', key);
                    hiddenField.setAttribute('value', params[key]);
                    form.appendChild(hiddenField);
                }
            }

            win.document.body.appendChild(form);

            // Submit and remove the form as in the old implementation
            if (windowReference !== undefined) {
                win.document.forms[0].submit();
                win.document.body.removeChild(form);
            } else {
                if (defaultTarget === target) {
                    setTimeout(function () {
                        form.submit();
                        win.document.body.removeChild(form);
                    }, 0);
                } else {
                    form.submit();
                    win.document.body.removeChild(form);
                }
            }

            return form;
        } catch (error) {
            this._logger.error('Error adding form to body', error);
            throw error;
        }
    }
}
